import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Lock, LogOut, Mail, Zap } from 'lucide-react';

export default function UpgradeRequired() {
  const { logout, gym } = useAuth();

  const isExpired = gym?.planStatus === 'EXPIRED';
  const planName = gym?.planType === 'TRIAL' ? 'Free Trial' : 'Subscription';

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-gray-50 dark:bg-[#0a0a0a]">
      {/* Background patterns */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 right-[-10%] w-[500px] h-[500px] bg-red-500/10 rounded-full blur-[100px] mix-blend-screen pointer-events-none" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[100px] mix-blend-screen pointer-events-none" />
      </div>

      <div className="relative z-10 w-full max-w-md p-8 overflow-hidden text-center border shadow-2xl bg-white dark:bg-[#121212] border-gray-200 dark:border-gray-800 rounded-2xl">
        {/* Top accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-500" />

        <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 rounded-full shadow-inner bg-red-50 dark:bg-red-500/10 shadow-red-500/20">
          <Lock size={32} className="text-red-500" />
        </div>

        <h2 className="mb-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          {isExpired ? `Your ${planName} Has Expired` : 'Access Restricted'}
        </h2>

        <p className="mb-8 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          {isExpired
            ? 'Your dashboard and features have been temporarily locked. Please upgrade to a paid plan to restore full access to Fitxero and continue managing your gym.'
            : 'You currently do not have an active subscription allowing access to the platform.'}
        </p>

        <div className="flex flex-col gap-3">
          {/* Upgrade Button */}
          <button
            type="button"
            onClick={() => (window.location.href = '/#pricing')}
            className="flex items-center justify-center w-full gap-2 px-6 py-3.5 text-sm font-bold text-white transition-all rounded-xl hover:-translate-y-0.5 shadow-lg shadow-indigo-500/30 bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-indigo-500/40"
          >
            <Zap size={16} />
            Upgrade Plan
          </button>

          <div className="flex gap-3 mt-1">
            {/* Contact Support */}
            <button
              type="button"
              onClick={() => window.open('mailto:support@fitxero.com', '_blank')}
              className="flex items-center justify-center w-full gap-2 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors border border-gray-200 bg-gray-50 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:border-gray-800 dark:bg-gray-800/50 dark:hover:bg-gray-800"
            >
              <Mail size={15} className="text-gray-400" />
              Support
            </button>

            {/* Logout */}
            <button
              type="button"
              onClick={logout}
              className="flex items-center justify-center w-full gap-2 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors border border-gray-200 bg-gray-50 hover:bg-red-50 hover:text-red-600 hover:border-red-200 rounded-xl dark:text-gray-300 dark:border-gray-800 dark:bg-gray-800/50 dark:hover:bg-red-500/10 dark:hover:text-red-400 dark:hover:border-red-500/20"
            >
              <LogOut size={15} />
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
